'use client';

import { useHashParameters } from '@packages/modules/src/hooks/useHashParameters/useHashParameters';
import { ExtendedRecoLink } from '@packages/recommendations/src/components/ExtendedRecoLink/ExtendedRecoLink.client';
import { PrudsysProductsRecoSlider } from '@packages/recommendations/src/components/ProductsRecoSliders/PrudsysProductsRecoSlider/PrudsysProductsRecoSlider.client';
import { useAbTesting } from '@packages/shared/src/abtesting/useAbTesting/useAbTesting';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useIntl } from 'react-intl';
import type { RawProductData } from '../../../types';

export type PrudsysProductsRecommendationsProps = {
  defaultCategory: RawProductData['defaultCategory'];
  orderNumber: RawProductData['orderNumber'];
  brand: RawProductData['brand'];
  /** can be removed after ab test */
  abTest: {
    isAbTestActive: boolean;
    withExtendedSlot: boolean;
    testId: string;
  };
};

/**
 * The `PrudsysProductsRecommendations` component is used as a wrapper for the `PrudsysProductsRecoSlider` component.
 * It displays the recommendations for the prudsysProducts Query.
 * */
export const PrudsysProductsRecommendations = ({
  brand,
  defaultCategory,
  orderNumber,
  abTest,
}: PrudsysProductsRecommendationsProps) => {
  const { category = '' } = useHashParameters();
  const { formatMessage } = useIntl();
  const {
    product: { hasTransparentSection },
  } = useConfig();

  // can be removed after ab test
  const { setOutcome, setRelevantProduct } = useAbTesting();
  const { isAbTestActive, withExtendedSlot, testId } = abTest;

  return (
    <PrudsysProductsRecoSlider
      brand={brand?.name ?? ''}
      categoryIds={[defaultCategory ?? category]}
      productId={orderNumber}
      pageTemplate="detailview"
      fetchOnThreshold
      elementIndex={0}
      title={formatMessage({
        defaultMessage: 'Andere Kunden interessierte auch',
        id: 'rde.prudsys.headline-fallback',
      })}
      key={`${orderNumber}0`}
      {...(hasTransparentSection && {
        backgroundColor: 'transparent',
      })}
      extendedSlot={
        withExtendedSlot
          ? (link) => (
              <ExtendedRecoLink
                link={link}
                onClick={() => {
                  setOutcome(testId, {
                    COF: 1,
                  });
                }}
              />
            )
          : undefined
      }
      onSeen={() =>
        isAbTestActive &&
        setOutcome(testId, {
          SEEN_RECO: 1,
        })
      }
      onProductClick={(akl) => {
        setRelevantProduct(testId, akl);
      }}
    />
  );
};
